/**
 * Which games exist, how to fetch one, and enough about each to list it
 * without fetching it.
 *
 * TAD-001 open question 1 asked whether `platform/` needs a registry. While the
 * shell only had to know that `match3` was a valid URL segment, the honest
 * answer was no and `KNOWN_GAME_IDS` in `shell/routes.ts` was enough. It is now
 * yes, for a reason that is not "one day there will be more games": the shell
 * has to turn a `gameId` from a URL into a `import()` of that game's chunk, and
 * a dynamic import needs a literal specifier the bundler can see. There is no
 * way to write `import(\`@games/${id}\`)` and still get code splitting.
 *
 * So this file is the one place that names game modules, and every entry's
 * `load` is a thunk. **Nothing here may become a static import.** A static
 * import collapses the split, the app keeps working perfectly, and only
 * `scripts/check-budget.mjs` notices — which is why it fails CI on it
 * (CLAUDE.md rule 4).
 *
 * `name`, `description` and `levelCount` are a second, hand-maintained copy of
 * fields `GameModule` already carries (SHELL-020, SHELL-021, SHELL-027):
 * `Picker.tsx` reads them from here so viewing the picker does not fetch every
 * registered game's chunk just to list it. The copy is kept honest by
 * `game-registry.test.ts`'s per-game loop, which loads the real module and
 * asserts these three fields agree with it — a test that fails the moment
 * either copy changes without the other, rather than a comment asking someone
 * to remember.
 */

import type { GameModule } from '@contracts/game-module';

type GameLoader = () => Promise<{ default: GameModule }>;

interface RegisteredGame {
  name: string;
  description: string;
  levelCount: number;
  load: GameLoader;
}

const GAMES: Readonly<Record<string, RegisteredGame>> = {
  demo: {
    name: 'Demo',
    description: 'A card you tap. Proves the shell can host a game it knows nothing about.',
    levelCount: 2,
    load: () => import('@games/demo'),
  },
  match3: {
    name: 'Match-3',
    description: 'Swap gems to clear runs, trigger specials and beat the board.',
    levelCount: 11,
    load: () => import('@games/match3'),
  },
};

/**
 * The games the shell will route to, in the order a picker should list them.
 *
 * `match3` joined here in Sprint 5a stage 5. Between Sprint 0 (its level data
 * shipped and passed conformance) and now, it was deliberately absent: a
 * game whose tile leads to an error is how a shell starts looking more
 * finished than it is, the failure both previous passes are made of. It was
 * kept out until there was a module behind it and an exit condition proving
 * the whole path -- level select to a reported win -- actually works.
 */
export const KNOWN_GAME_IDS: readonly string[] = Object.keys(GAMES);

export function isKnownGame(gameId: string): boolean {
  return Object.hasOwn(GAMES, gameId);
}

export interface GameInfo {
  name: string;
  description: string;
  levelCount: number;
}

/**
 * The registry's copy of a game's display metadata, or `undefined` for an
 * unregistered id. See this file's header for why it exists and how it is
 * kept from drifting off the module's own copy.
 */
export function gameInfo(gameId: string): GameInfo | undefined {
  const entry = GAMES[gameId];
  if (!entry) return undefined;
  return { name: entry.name, description: entry.description, levelCount: entry.levelCount };
}

/**
 * Fetches a game's module, or `undefined` if no such game is registered.
 *
 * Two different failures, kept apart on purpose. An unknown id is a bad URL and
 * belongs to `SHELL-013`; a registered id whose chunk will not download is a
 * network or deployment failure and belongs to `SHELL-024`. Returning
 * `undefined` for the first lets the caller answer it without a `catch` that
 * would swallow the second.
 */
export function loadGame(gameId: string): Promise<{ default: GameModule }> | undefined {
  return GAMES[gameId]?.load();
}
